import React from 'react'
import Decimal from 'decimal.js'
import { Grid, GridCell } from '@mobi/component-library/Legacy/Grid'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { observeImmutable } from '@core/Components/HOCs'
import { calculateProjectedReward } from '@core/Utils/betting/superPicks/helpers/rewardCalculator'
import { BetSpecialOffer } from '@core/Utils/betting/superPicks/models/BetSpecialOffer'
import { PriceDetails } from '@core/Utils/betting/superPicks/models/PriceDetails'
import { Stake } from '@core/Utils/betting/superPicks/models/Stake'
import { useInducementService } from '@core/Utils/hooks/useInducementService'
import { HeaderBg, HeaderIconStyled, HeaderStyled, SuperPickStyled } from './SuperPick.styles'
import { SuperPickButton } from './SuperPickButton'
import { SuperPickState, SuperPickStateRecord, state$ as superPickState$ } from './driver'
import { LoadSuperPicks } from './signals'

interface SuperPickProps {
  investmentWin: number
  investmentPlace: number
  winPrice: number
  placePrice: number
  isDisabled: boolean
  isVisible: boolean
}

export const SuperPickComponent = ({
  specialOffers,
  investmentWin,
  investmentPlace,
  winPrice,
  placePrice,
  isVisible,
  isDisabled,
}: Pick<SuperPickState, 'specialOffers'> & SuperPickProps): JSX.Element | null => {
  if (!specialOffers || !specialOffers.length || !isVisible) {
    return null
  }

  const stakeObj: Stake = Stake.normalise({
    Win: new Decimal(investmentWin || 0),
    Place: new Decimal(investmentPlace || 0),
  })
  const priceDetails: PriceDetails = PriceDetails.normalise({
    Win: { DollarReturn: new Decimal(winPrice || 0) },
    Place: { DollarReturn: new Decimal(placePrice || 0) },
  })

  return (
    <SuperPickStyled>
      <HeaderStyled>
        <HeaderBg>
          <HeaderIconStyled>
            <Icon type='specials' />
          </HeaderIconStyled>
          SuperPick
        </HeaderBg>
      </HeaderStyled>
      <Grid padding='0.2rem'>
        {specialOffers.map(specialOffer => {
          const newSpecialOffer = BetSpecialOffer.normalise(specialOffer)
          const projectedReward = calculateProjectedReward(
            newSpecialOffer as BetSpecialOffer,
            stakeObj,
            priceDetails
          ).toNumber()
          return (
            <GridCell key={newSpecialOffer.specialSeq}>
              <SuperPickButton
                specialOffer={newSpecialOffer}
                projectedReward={projectedReward}
                isDisabled={isDisabled}
              />
            </GridCell>
          )
        })}
      </Grid>
    </SuperPickStyled>
  )
}

const SuperPickObserver = observeImmutable<SuperPickStateRecord, SuperPickProps>(
  superPickState$,
  ({ record, investmentWin, investmentPlace, winPrice, placePrice, isVisible, isDisabled }) => {
    React.useEffect(() => {
      LoadSuperPicks()
    }, [])

    const specialOffers = (record.toJS() as SuperPickState).specialOffers

    return (
      <SuperPickComponent
        specialOffers={specialOffers}
        investmentWin={investmentWin}
        investmentPlace={investmentPlace}
        winPrice={winPrice}
        placePrice={placePrice}
        isDisabled={isDisabled}
        isVisible={isVisible}
      />
    )
  }
)

export const SuperPickWithInducementCheck: React.FC<SuperPickProps> = ({
  investmentWin,
  investmentPlace,
  winPrice,
  placePrice,
  isDisabled,
  isVisible,
}) => {
  const canShowInducements = useInducementService()
  return canShowInducements ? (
    <SuperPickObserver
      investmentWin={investmentWin}
      investmentPlace={investmentPlace}
      winPrice={winPrice}
      placePrice={placePrice}
      isDisabled={isDisabled}
      isVisible={isVisible}
    />
  ) : null
}
